import { ITimeTablePeriod } from "@base/modules/appointment/appointment.interface";
import { IDeliveryAddress } from "@base/modules/customerDeliveryAddress/customerDeliveryAddress.interface";
import { normalizeProvinceCode, OTHER_PROVINCE_CODE } from "@base/modules/doctor/doctorExtraInfo";
import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";
import { ISite, ISiteBranch } from "@base/modules/site/site.interface";
import { ISpecialization } from "@base/modules/specialization/interface/specialization.interface";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import { BookingWindow, extraInfoOf, flagValue, isAbicPackage, isBlank, isInsuranceSeller, PackageRoute, up } from "./packageDetailRules";
import { ISpecialPackage, ISpecialPackageDetail, ISpecialPackageDetailImplement, ISpecialServiceDetailItem } from "./patientSpecialPackage.interface";

/** Rules of the package booking form (`dat-lich-goi-web-spec.md` §12). */

export interface PackageBookingContext {
  route: PackageRoute;
  pkg: ISpecialPackage;
  service: ISpecialPackageDetail;
  visit: ISpecialPackageDetailImplement | null;
  /** Who is examined: the package owner or a relative. */
  record: IPatientRecord;
  site: ISite;
  /** Only a regular package books at a branch (the one that sold it). */
  branch: ISiteBranch | null;
  window: BookingWindow;
  /** Group checkup: the items chosen, mandatory ones included. */
  groupExamItems: ISpecialServiceDetailItem[];
}

const ONLINE_TYPES = ["ONLINE_SERVICE", "ONLINE"];

export const isOnlineServiceType = (type?: string | null) => ONLINE_TYPES.includes(up(type));
export const isOnlineContext = (context: PackageBookingContext) => isOnlineServiceType(context.service.type);
export const isInsuranceContext = (context: PackageBookingContext) => isInsuranceSeller(context.pkg);
export const isAbicContext = (context: PackageBookingContext) => isAbicPackage(context.pkg);

const firstText = (...values: (string | null | undefined)[]) => values.map((value) => (value ?? "").trim()).find(Boolean) ?? "";

/** The service code - also what is sent as `service_code` when listing doctors. */
export const serviceCodeOf = (context: PackageBookingContext) =>
  firstText(context.service.code, context.service.item_code, context.pkg.code, context.pkg.package_code);

/** A group checkup is named after the package, anything else after its service. */
export const serviceNameOf = (context: PackageBookingContext) =>
  context.route === "GROUP_EXAM"
    ? firstText(context.pkg.name, context.pkg.package_name, context.service.item_name)
    : firstText(context.service.item_name, context.pkg.name, context.pkg.package_name);

export const serviceDescOf = (context: PackageBookingContext) =>
  firstText(context.service.description, context.service.note, context.pkg.description);
export const serviceNoteOf = (context: PackageBookingContext) => firstText(context.service.note, context.pkg.note);

/** Insurance packages list every doctor in one page so none is lost to the package filter. */
export const doctorPageSize = (context: PackageBookingContext) => (isInsuranceContext(context) ? 200 : 15);

//! Doctors
const doctorExtra = (doctor: IDoctor) => extraInfoOf(doctor);

const asRecordList = (value: unknown): Record<string, unknown>[] => {
  if (Array.isArray(value)) {
    return value as Record<string, unknown>[];
  }

  if (value && typeof value === "object") {
    return [value as Record<string, unknown>];
  }

  if (typeof value === "string" && value.trim()) {
    try {
      return asRecordList(JSON.parse(value));
    } catch {
      return [];
    }
  }

  return [];
};

/** Step 1: does this doctor serve the PACKAGE and the SERVICE being booked. */
export const matchesPackage = (doctor: IDoctor, pkg: ISpecialPackage, serviceCode: string) => {
  const list = asRecordList(doctorExtra(doctor).special_packages);

  if (!list.length) {
    return true;
  }

  const packageCodes = [pkg.package_code, pkg.code].map(up).filter(Boolean);

  if (!packageCodes.length) {
    return true;
  }

  const mine = list.filter((entry) => packageCodes.includes(up(entry.code as string)));

  if (!mine.length) {
    return false;
  }

  const code = up(serviceCode);
  // A code that is not one of the package's services counts as "no service picked".
  const serviceInPackage = (pkg.patient_special_package_details ?? []).some((service) => [service.code, service.item_code].map(up).includes(code));

  return mine.some((entry) => {
    const details = asRecordList(entry.site_special_package_details);

    if (!details.length) {
      return true;
    }

    return details.some((detail) => flagValue(detail.selected) === 1 && (!serviceInPackage || up(detail.code as string) === code));
  });
};

export interface DoctorGroup {
  code: string;
  name: string;
}

export const doctorGroups = (doctor: IDoctor, language?: string): DoctorGroup[] =>
  asRecordList(doctorExtra(doctor).doctor_group)
    .map((group) => ({
      code: up(group.group_code as string),
      name:
        [group[`group_name_${language ?? "vi"}`], group.group_name_vi, group.group_name_en, group.group_name, group.group_code]
          .map((value) => (typeof value === "string" ? value.trim() : ""))
          .find(Boolean) ?? "",
    }))
    .filter((group) => group.code);

/**
 * Step 2, on the result of step 1: when any eligible doctor has a group, the ungrouped ones go and
 * the groups become chips; otherwise every eligible doctor stays and there are no chips.
 */
export const filterByPackageRules = (doctors: IDoctor[], context: PackageBookingContext, language?: string) => {
  const eligible = doctors.filter((doctor) => matchesPackage(doctor, context.pkg, serviceCodeOf(context)));
  const withGroup = eligible.filter((doctor) => doctorGroups(doctor, language).length > 0);

  if (!withGroup.length) {
    return { doctors: eligible, groups: [] as DoctorGroup[] };
  }

  const groups: DoctorGroup[] = [];

  withGroup.forEach((doctor) =>
    doctorGroups(doctor, language).forEach((group) => {
      if (!groups.some((item) => item.code === group.code)) {
        groups.push(group);
      }
    }),
  );

  return { doctors: withGroup, groups };
};

export const doctorsInGroup = (doctors: IDoctor[], groupCode: string | undefined, language?: string) =>
  groupCode ? doctors.filter((doctor) => doctorGroups(doctor, language).some((group) => group.code === groupCode)) : doctors;

/**
 * Which group starts selected: the previous one if still offered, else the first - except on ABIC
 * with two or more groups, where the patient has to choose (null).
 */
export const initialGroup = (groups: DoctorGroup[], previous: string | undefined, abic: boolean): string | undefined => {
  if (previous && groups.some((group) => group.code === previous)) {
    return previous;
  }

  if (!groups.length || (abic && groups.length >= 2)) {
    return undefined;
  }

  return groups[0].code;
};

const foldName = (name?: string) => (name ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").trim().toLowerCase();

/** The placeholder "doctor" an insurer uses when no one in particular is assigned. */
export const isDefaultDoctor = (doctor?: IDoctor) => foldName(doctor?.doctor_name) === "mac dinh";

/** The "Details" pill: ABIC only, never on the placeholder, and not when the insurer hides it. */
export const canViewDoctorInfo = (context: PackageBookingContext, doctor: IDoctor) =>
  isAbicContext(context) && !isDefaultDoctor(doctor) && flagValue(doctorExtra(doctor).doctor_info_hidden) !== 1;

//! ABIC: the service type follows the doctor group, and so does the service address
export const HOME_CARE_GROUP = "1_HOME_CARE";
export const OFFLINE_GROUP = "2_OFFLINE";

/** ABIC only: home-care and clinic groups decide the real service type; anything else keeps the service's. */
export const effectiveServiceType = (context: PackageBookingContext, groupCode?: string) => {
  const type = context.service.type ?? "";

  if (!isAbicContext(context)) {
    return type;
  }

  if (groupCode === HOME_CARE_GROUP) {
    return "HOMECARE_SERVICE";
  }

  if (groupCode === OFFLINE_GROUP) {
    return "OFFLINE_SERVICE";
  }

  return type;
};

export type ServiceAddressMode = "HOME" | "FACILITY" | "NONE";

export interface IPackageFacilityOption {
  state?: string;
  name?: string;
  address?: string;
}

/** A doctor's partner clinics - nested JSON - without the entries that have no address. */
export const doctorFacilities = (doctor?: IDoctor): IPackageFacilityOption[] =>
  doctor ? (asRecordList(doctorExtra(doctor).facilities) as IPackageFacilityOption[]).filter((facility) => !isBlank(facility.address)) : [];

/**
 * ABIC at a clinic, once a group is chosen (or there are none): home care asks for the patient's
 * address, a doctor with partner clinics asks for one of them, anything else asks nothing.
 */
export const serviceAddressMode = (
  context: PackageBookingContext,
  doctor: IDoctor | undefined,
  groupCode: string | undefined,
  hasGroups: boolean,
): ServiceAddressMode => {
  if (!isAbicContext(context) || isOnlineContext(context) || (hasGroups && !groupCode)) {
    return "NONE";
  }

  const type = up(effectiveServiceType(context, groupCode));

  if (type === "HOMECARE_SERVICE" || type === "HOMECARE") {
    return "HOME";
  }

  return doctorFacilities(doctor).length ? "FACILITY" : "NONE";
};

/** After a home address is picked: the doctor of its province, else the "other provinces" one. */
export const doctorForProvince = (doctors: IDoctor[], provinceCode?: string): IDoctor | undefined => {
  const wanted = normalizeProvinceCode(provinceCode);

  if (!wanted) {
    return undefined;
  }

  const provinceOf = (doctor: IDoctor) => normalizeProvinceCode(doctorExtra(doctor).province_code);

  return doctors.find((doctor) => provinceOf(doctor) === wanted) ?? doctors.find((doctor) => provinceOf(doctor) === OTHER_PROVINCE_CODE);
};

/** Both sides known, the doctor is not the catch-all, and the provinces differ. */
export const hasProvinceMismatch = (doctor: IDoctor | undefined, address: IDeliveryAddress | undefined) => {
  const doctorProvince = doctor ? normalizeProvinceCode(doctorExtra(doctor).province_code) : undefined;
  const addressProvince = normalizeProvinceCode(address?.customer_delivery_province_code);

  return !!doctorProvince && !!addressProvince && doctorProvince !== OTHER_PROVINCE_CODE && doctorProvince !== addressProvince;
};

//! Extra fields
export interface IExtraFieldConfig {
  field_name?: string;
  field_type?: string;
  required?: unknown;
  label?: Record<string, string> | string;
  [key: string]: unknown;
}

const extraFieldsOf = (owner?: { extra_info?: unknown }) =>
  (asRecordList(extraInfoOf(owner).extra_fields) as IExtraFieldConfig[]).filter((field) => !isBlank(field.field_name as string));

/** The service's own `extra_fields`, else the package's, else another service's. */
export const extraFieldConfigs = (pkg: ISpecialPackage, service: ISpecialPackageDetail): IExtraFieldConfig[] => {
  const own = extraFieldsOf(service);

  if (own.length) {
    return own;
  }

  const fromPackage = extraFieldsOf(pkg);

  if (fromPackage.length) {
    return fromPackage;
  }

  for (const other of pkg.patient_special_package_details ?? []) {
    const fields = extraFieldsOf(other);

    if (fields.length) {
      return fields;
    }
  }

  return [];
};

export type ExtraFieldKind = "ADDRESS" | "SPECIALIZATION" | "TEXT";

/**
 * The input follows `field_name`, not `field_type` (which the backend misspells). "address" is
 * tested first: `expected_site_address` contains "site" too.
 */
export const extraFieldKind = (field: IExtraFieldConfig): ExtraFieldKind => {
  const name = String(field.field_name ?? "").toLowerCase();

  if (name.includes("address")) {
    return "ADDRESS";
  }

  return name.includes("specializ") ? "SPECIALIZATION" : "TEXT";
};

export const isExtraFieldRequired = (field: IExtraFieldConfig) => {
  const required = field.required;

  return flagValue(required) === 1 || (typeof required === "string" && required.trim().toLowerCase() === "yes");
};

export const extraFieldLabel = (field: IExtraFieldConfig, language?: string) => {
  const label = field.label;

  if (label && typeof label === "object") {
    return (
      [label[language ?? "vi"], label.vi, label.en].map((value) => (typeof value === "string" ? value.trim() : "")).find(Boolean) ??
      String(field.field_name)
    );
  }

  return typeof label === "string" && label.trim() ? label.trim() : String(field.field_name);
};

export interface IExtraAddressValue {
  provinceCode?: string;
  provinceName?: string;
  wardCode?: string;
  wardName?: string;
}

export type ExtraFieldValue = string | IExtraAddressValue | ISpecialization | undefined;

/** Every value goes up as a STRING: an address and a specialty as JSON, text trimmed. */
export const encodeExtraFieldValue = (field: IExtraFieldConfig, value: ExtraFieldValue): string => {
  switch (extraFieldKind(field)) {
    case "ADDRESS": {
      const address = (value ?? {}) as IExtraAddressValue;

      return JSON.stringify({
        administrative_province_code: address.provinceCode ?? "",
        administrative_province_name: address.provinceName ?? "",
        administrative_ward_code: address.wardCode ?? "",
        administrative_ward_name: address.wardName ?? "",
      });
    }
    case "SPECIALIZATION": {
      const specialization = value as ISpecialization | undefined;

      if (!specialization?.specialization_code) {
        return "";
      }

      return JSON.stringify({
        active: specialization.active ?? true,
        description: specialization.description ?? "",
        id: specialization.id ?? 0,
        is_active: specialization.is_active ?? true,
        specialization_code: specialization.specialization_code,
        specialization_name: specialization.specialization_name ?? "",
      });
    }
    default:
      return typeof value === "string" ? value.trim() : "";
  }
};

const sortedKeys = (_key: string, value: unknown) =>
  value && typeof value === "object" && !Array.isArray(value)
    ? Object.keys(value as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (value as Record<string, unknown>)[key];
          return acc;
        }, {})
    : value;

/** Every configured field - empty ones too - as the backend's own object plus `value`. */
export const encodeExtraFields = (configs: IExtraFieldConfig[], values: Record<string, ExtraFieldValue>): string | undefined => {
  if (!configs.length) {
    return undefined;
  }

  return JSON.stringify(
    configs.map((config) => ({ ...config, value: encodeExtraFieldValue(config, values[String(config.field_name)]) })),
    sortedKeys,
  );
};

export type ExtraFieldError = { field: string; label: string; part?: "PROVINCE" | "WARD" };

/** The first required field left empty, if any. */
export const firstMissingExtraField = (
  configs: IExtraFieldConfig[],
  values: Record<string, ExtraFieldValue>,
  language?: string,
): ExtraFieldError | null => {
  for (const config of configs) {
    if (!isExtraFieldRequired(config)) {
      continue;
    }

    const name = String(config.field_name);
    const label = extraFieldLabel(config, language);
    const value = values[name];

    switch (extraFieldKind(config)) {
      case "ADDRESS": {
        const address = (value ?? {}) as IExtraAddressValue;

        if (isBlank(address.provinceCode)) {
          return { field: name, label, part: "PROVINCE" };
        }

        if (isBlank(address.wardCode)) {
          return { field: name, label, part: "WARD" };
        }

        break;
      }
      case "SPECIALIZATION":
        if (isBlank((value as ISpecialization | undefined)?.specialization_code)) {
          return { field: name, label };
        }
        break;
      default:
        if (isBlank(value as string)) {
          return { field: name, label };
        }
    }
  }

  return null;
};

//! Submit
export interface IPackageBookingForm {
  date?: string;
  slot?: ITimeTablePeriod;
  doctor?: IDoctor;
  symptoms: string;
}

/** Symptoms are required (and prefilled with the service name) everywhere but on insurance packages. */
export const symptomsRequired = (context: PackageBookingContext) => !isInsuranceContext(context);

export const initialSymptoms = (context: PackageBookingContext) => (symptomsRequired(context) ? (context.service.item_name ?? "") : "");

export const canSubmitPackageBooking = (context: PackageBookingContext, form: IPackageBookingForm) =>
  !!form.date &&
  (!symptomsRequired(context) || !!form.symptoms.trim()) &&
  !!form.slot &&
  !isBlank(form.doctor?.doctor_code) &&
  !!serviceNameOf(context);

const ADDRESS_KEYS: (keyof IDeliveryAddress)[] = [
  "customer_delivery_address",
  "customer_delivery_name",
  "customer_delivery_phone",
  "customer_delivery_province_code",
  "customer_delivery_province_name",
  "customer_delivery_ward_code",
  "customer_delivery_ward_name",
  "id",
  "primary",
  "user_key",
];

/** The home address as the JSON string the backend stores, keys a-z. */
export const encodeCustomerAddress = (address: IDeliveryAddress) =>
  JSON.stringify(
    ADDRESS_KEYS.reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = address[key] ?? (key === "primary" ? false : key === "id" ? 0 : "");
      return acc;
    }, {}),
  );

export interface ICreateFromPackageFields {
  date: string;
  slot: ITimeTablePeriod;
  doctor: IDoctor;
  symptoms: string;
  /** ABIC: already switched by the doctor group. */
  serviceType: string;
  customerAddressJSON?: string;
  /** ABIC clinic: the clinic's `address` string, never sent for an online service. */
  facilityAddress?: string;
  extraFieldsJSON?: string;
}

/** Body of `POST /doctor/appointment/create-from-package` (§12.14). */
export const buildCreateFromPackage = (context: PackageBookingContext, fields: ICreateFromPackageFields) => {
  const online = isOnlineContext(context);
  const service: Record<string, unknown> = {
    id: 0,
    service_id: 0,
    name: serviceNameOf(context),
    description: serviceDescOf(context),
    note: serviceNoteOf(context),
    amount: 0,
    real_amount: 0,
    code: serviceCodeOf(context),
    patient_special_package_detail_implement_id: context.visit?.id ?? 0,
    service_type: fields.serviceType,
    package_type: context.pkg.package_type ?? "",
    supported_specialization: "",
  };

  if (context.route === "GROUP_EXAM" && context.groupExamItems.length) {
    service.service_items = JSON.stringify(
      context.groupExamItems.map((item) => ({ name: item.name ?? "", amount: item.amount ?? 0, selected: true, mandatory: !!item.mandatory })),
    );
  }

  if (fields.customerAddressJSON) {
    service.customer_address = fields.customerAddressJSON;
  }

  if (fields.facilityAddress && !online) {
    service.facility_address = fields.facilityAddress;
  }

  if (fields.extraFieldsJSON) {
    service.extra_fields = fields.extraFieldsJSON;
  }

  const body: Record<string, unknown> = {
    appointment_date: fields.date,
    symptoms: fields.symptoms.trim(),
    patient_record: context.record,
    time_table_period: fields.slot,
    doctor: fields.doctor,
    doctor_code: fields.doctor.doctor_code ?? "",
    appointment_promotions: [],
    key: "",
    is_free: false,
    appointment_used_services: [service],
    site_code: context.site.code ?? "",
    site_name: context.site.name ?? "",
    appointment_type: online ? "ONLINE" : "OFFLINE",
    contact_phone: context.record.contact_phone || context.record.patient_phone_number || "",
  };

  if (context.branch?.code && context.branch?.name) {
    body.site_branch_code = context.branch.code;
    body.site_branch_name = context.branch.name;
  }

  return body;
};

/** Attachments only go up for an appointment still open. */
export const canUploadAttachments = (appointment?: { key?: string; status?: string }) =>
  !!appointment?.key && ["", "NEW", "PENDING", "IN_PROGRESS"].includes(up(appointment.status));
